import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { locationApi } from 'components/location/LocationApi';
import { isLocations, Location, LocationState } from 'components/location/models/LocationModel';
import {
    MESSAGE_LOCATION_ACTIVATE_ERROR,
    MESSAGE_LOCATION_ACTIVATE_SUCCESS,
    MESSAGE_LOCATION_DELETE_ERROR,
    MESSAGE_LOCATION_DELETE_SUCCESS,
    MESSAGE_LOCATION_RETIRE_ERROR,
    MESSAGE_LOCATION_RETIRE_SUCCESS
} from 'config/messages';
import logger from 'lib/logger';
import { enqueueSnackbar } from 'notistack';
import { updateArrayItem } from 'lib/helpers';
import type { AppDispatch } from '../store';

const LOCATIONS_PER_PAGE = '100';

export interface LocationsState {
    locations?: Location[];
    summary?: { total: number; count: number; pages: number; page: number };
    error: string;
    isLoading: boolean;
    locationIdToLocationTitle?: Map<string, string>;
}

export const initialState: LocationsState = {
    locations: [],
    locationIdToLocationTitle: new Map(),
    error: null,
    summary: null,
    isLoading: false
};

const locationsSlice = createSlice({
    name: 'locations',
    initialState,
    reducers: {
        startRequest(state) {
            state.error = null;
            state.isLoading = true;
        },
        endRequest(state) {
            state.error = null;
            state.isLoading = false;
        },
        getLocationsSuccess(
            state,
            action: PayloadAction<{
                locations: Location[];
                summary: LocationsState['summary'];
            }>
        ) {
            state.locations = action.payload.locations;
            state.summary = action.payload.summary;
            state.error = null;
        },
        getAllLocationTitlesSuccess(
            state,
            action: PayloadAction<{
                locations: Location[];
                locationIdToTitleMap: Map<string, string>;
            }>
        ) {
            state.locations = action.payload.locations;
            state.locationIdToLocationTitle = action.payload.locationIdToTitleMap;
        },
        getLocationsError(state, action: PayloadAction<{ error: string }>) {
            state.error = action.payload.error;
            state.locations = null;
            state.summary = null;
        },
        loadLocationsSuccess(
            state,
            action: PayloadAction<{
                locations: Location[];
                summary: LocationsState['summary'];
            }>
        ) {
            state.summary = action.payload.summary;
            state.error = null;
            state.locations = [...state.locations, ...action.payload.locations];
        },
        deleteLocationByIndex(state, action: PayloadAction<{ index: number }>) {
            const { index } = action.payload;
            state.locations = [...state.locations.slice(0, index), ...state.locations.slice(index + 1)];
        },
        updateLocation(state, action: PayloadAction<{ index: number; location: Location }>) {
            const { index, location } = action.payload;
            state.locations = updateArrayItem(state.locations, index, location);
        }
    }
});

export const {
    startRequest,
    endRequest,
    getLocationsSuccess,
    getAllLocationTitlesSuccess,
    getLocationsError,
    loadLocationsSuccess,
    deleteLocationByIndex,
    updateLocation
} = locationsSlice.actions;

// --- Thunks ---

export const getAllLocationTitles = async (dispatch: AppDispatch) => {
    const getAllLocationsRecursive = async (allLocations: Location[] = [], page = 1): Promise<Location[]> => {
        const queryParameters = [
            { key: 'state', value: 'ACTIVE,PENDING,RETIRED' },
            { key: 'page', value: page },
            { key: 'per_page', value: 100 }
        ];
        const response = await locationApi.getList({ queryParameters });
        const { locations, summary } = response.body;
        allLocations.push(...locations);

        if (allLocations.length !== summary.total) {
            return getAllLocationsRecursive(allLocations, summary.page + 1);
        }

        return allLocations;
    };

    const allLocations: Location[] = await getAllLocationsRecursive();
    const locationIdToTitleMap = allLocations.reduce((acc, location) => {
        acc.set(location._id, location.title);

        return acc;
    }, new Map());

    dispatch(
        getAllLocationTitlesSuccess({
            locations: allLocations,
            locationIdToTitleMap
        })
    );
    return allLocations;
};

export const getLocations = (title?: string) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const queryParameters = [
        { key: 'page', value: '0' },
        { key: 'per_page', value: LOCATIONS_PER_PAGE },
        { key: 'state', value: 'ACTIVE,PENDING,RETIRED' }
    ];
    if (!!title) {
        queryParameters.push({
            key: 'title',
            value: title
        });
    }
    const response = await locationApi.getList({ queryParameters });
    const { locations, summary } = response.body;
    if (isLocations(locations)) {
        dispatch(getLocationsSuccess({ locations, summary }));
    } else {
        logger.error('TypeError: expected Location[], got:', locations);
        dispatch(getLocationsError({ error: 'Locations data violated' }));
    }
    dispatch(endRequest());
};

export const loadLocations =
    (locationStateSummary: LocationsState['summary'], title?: string) => async (dispatch: AppDispatch) => {
        dispatch(startRequest());
        if (locationStateSummary.page === locationStateSummary.pages) {
            return dispatch(endRequest());
        }
        const nextPage = locationStateSummary.page + 1;
        const queryParameters = [
            { key: 'page', value: String(nextPage) },
            { key: 'per_page', value: LOCATIONS_PER_PAGE },
            { key: 'state', value: 'ACTIVE,PENDING,RETIRED' }
        ];
        if (!!title) {
            queryParameters.push({
                key: 'title',
                value: title
            });
        }
        const response = await locationApi.getList({ queryParameters });
        const { locations, summary } = response.body;
        if (isLocations(locations)) {
            dispatch(loadLocationsSuccess({ locations, summary }));
        } else {
            logger.error('TypeError: expected Location[], got:', locations);
            dispatch(getLocationsError({ error: 'Locations data violated' }));
        }
        dispatch(endRequest());
    };

export const getAllLocations = () => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const queryParameters = [{ key: 'state', value: 'ACTIVE,PENDING,RETIRED' }];
    const response = await locationApi.getList({ queryParameters });
    const { locations, summary } = response.body;
    if (isLocations(locations)) {
        dispatch(getLocationsSuccess({ locations, summary }));
    } else {
        logger.error('TypeError: expected Location[], got:', locations);
        dispatch(getLocationsError({ error: 'Locations data violated' }));
    }
    dispatch(endRequest());

    return locations;
};

export const retireLocation = (location: Location, index: number) => async (dispatch: Dispatch) => {
    const { _id, ...rest } = location;
    dispatch(startRequest());
    const response = await locationApi.update(_id, { body: { ...rest, state: LocationState.RETIRED } });
    if (response.ok) {
        enqueueSnackbar(MESSAGE_LOCATION_RETIRE_SUCCESS(location.title), { variant: 'success' });
        dispatch(updateLocation({ index, location: response.body }));
    } else {
        enqueueSnackbar(MESSAGE_LOCATION_RETIRE_ERROR, { variant: 'error' });
    }
    dispatch(endRequest());
};

export const activateLocation = (location: Location, index: number) => async (dispatch: Dispatch) => {
    const { _id, ...rest } = location;
    dispatch(startRequest());
    const response = await locationApi.update(_id, { body: { ...rest, state: LocationState.ACTIVE } });
    if (response.ok) {
        enqueueSnackbar(MESSAGE_LOCATION_ACTIVATE_SUCCESS(location.title), { variant: 'success' });
        dispatch(updateLocation({ index, location: response.body }));
    } else {
        enqueueSnackbar(MESSAGE_LOCATION_ACTIVATE_ERROR, { variant: 'error' });
    }
    dispatch(endRequest());
};

export const deleteLocation = (locationId: string, index: number) => async (dispatch: Dispatch) => {
    dispatch(startRequest());
    const response = await locationApi.delete(locationId, { skipResponseBody: true });
    if (response.ok) {
        enqueueSnackbar(MESSAGE_LOCATION_DELETE_SUCCESS, { variant: 'success' });
        dispatch(deleteLocationByIndex({ index }));
    } else {
        enqueueSnackbar(MESSAGE_LOCATION_DELETE_ERROR, { variant: 'error' });
    }
    dispatch(endRequest());
};

export default locationsSlice.reducer;
